/**
 * Canonical TypeScript shapes for the `bess_alarm_rules` envelope (sent
 * to the edge as a SHARED_SCOPE TB attribute), the `bess_alarm_rules_status`
 * ACK (SERVER_SCOPE), and the `available_datapoints` catalog (SERVER_SCOPE).
 *
 * The runtime is untrusted (a TB widget or REST hand-edit could write
 * anything), so every read goes through a zod parser that drops invalid
 * entries while keeping the rest. We log to the console — never throw —
 * because the SPA must keep rendering even if one rule is malformed.
 */
import { z } from "zod";

import {
  ALLOWED_EXPRESSION_CALLS,
  MAX_DURATION_S,
  MAX_ESCALATE_AFTER_H,
  MAX_WINDOW_S,
  MAX_EXPRESSION_LENGTH,
  RULES_ATTRIBUTE_KEY,
  RULES_STATUS_ATTRIBUTE_KEY,
  AVAILABLE_DATAPOINTS_ATTRIBUTE_KEY,
  VALID_AGG_OPERATORS,
  VALID_COMPOSITE_MISSING_MODES,
  VALID_COMPOSITE_OPERATORS,
  VALID_DELIVERY_METHODS,
  VALID_NUMERIC_OPERATORS,
  VALID_PROPAGATE_LEVELS,
  VALID_RATE_DIRECTIONS,
  VALID_RULE_TYPES,
  VALID_SEVERITIES,
} from "./engineConstants";

// ---------------------------------------------------------------------------
// Common atoms
// ---------------------------------------------------------------------------

const durationS = z.coerce.number().min(0).max(MAX_DURATION_S);
const windowS = z.coerce.number().min(0).max(MAX_WINDOW_S);
const positiveWindowS = z.coerce.number().positive().max(MAX_WINDOW_S);
const severitySchema = z.enum(VALID_SEVERITIES);
const deliveryMethodSchema = z.enum(VALID_DELIVERY_METHODS).default("notification");

// v4 — per-rule notification (recipients + escalation + notify_on_clear).
// The cloud SPA carries INLINE email addresses (the operator typing
// real emails); recipient-group names belong to the EMS-Admin local
// surface and are not visible from the cloud. The edge resolver
// (`alarm_notification.resolve_notification`) accepts both forms.
const cloudEmailSchema = z
  .string()
  .trim()
  .min(3)
  .max(320)
  .regex(/^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/, {
    message: "must be a valid email address",
  });

const cloudEscalationSchema = z
  .object({
    after_h: z.coerce
      .number()
      .gt(0)
      .max(MAX_ESCALATE_AFTER_H),
    recipients: z.array(cloudEmailSchema).min(1),
  });

/**
 * v4 cloud-rule notification. Only the INLINE form is allowed on the
 * cloud side (no recipient-group references — the SPA does not see
 * EMS-Admin's local groups). Omitting `notification` entirely produces
 * a rule that still evaluates and emits a TB alarm but carries no
 * recipients — no email side effect.
 */
const cloudNotificationSchema = z
  .object({
    instant: z.array(cloudEmailSchema).min(1),
    escalation: cloudEscalationSchema.optional(),
    notify_on_clear: z.boolean().default(false),
  });

const propagateSchema = z.enum(VALID_PROPAGATE_LEVELS).default("TENANT");
const numericOperatorSchema = z.enum(VALID_NUMERIC_OPERATORS);
const rateDirectionSchema = z.enum(VALID_RATE_DIRECTIONS).default("both");
const compositeOperatorSchema = z.enum(VALID_COMPOSITE_OPERATORS);
const compositeMissingModeSchema = z
  .enum(VALID_COMPOSITE_MISSING_MODES)
  .default("false");
const aggOperatorSchema = z.enum(VALID_AGG_OPERATORS);

const weeklySchedule7x24 = z
  .array(z.array(z.boolean()).length(24))
  .length(7);

const expressionDatapointSchema = z.union([
  z.string().min(1),
  z.object({
    topic: z.string().min(1),
    agg: aggOperatorSchema,
    window_s: positiveWindowS,
  }),
]);
export type ExpressionDatapointSpec = z.infer<typeof expressionDatapointSchema>;

const expressionSourceSchema = z
  .string()
  .min(1)
  .max(MAX_EXPRESSION_LENGTH);

const activeWhenSchema = z
  .object({
    weeklySchedule: weeklySchedule7x24.optional(),
    dateRange: z
      .object({
        startMs: z.number().int().nonnegative().optional(),
        endMs: z.number().int().nonnegative().optional(),
      })
      .refine(
        (v) =>
          (v.startMs !== undefined || v.endMs !== undefined) &&
          (v.startMs === undefined ||
            v.endMs === undefined ||
            v.startMs < v.endMs),
        { message: "dateRange needs at least startMs or endMs and startMs < endMs" },
      )
      .optional(),
  })
  .refine(
    (v) => v.weeklySchedule !== undefined || v.dateRange !== undefined,
    { message: "active_when must include weeklySchedule or dateRange" },
  );
export type ActiveWhen = z.infer<typeof activeWhenSchema>;

// ---------------------------------------------------------------------------
// Per-type config schemas
// ---------------------------------------------------------------------------

const thresholdConfig = z.object({
  operator: numericOperatorSchema,
  threshold: z.coerce.number(),
  hysteresis: z.coerce.number().nonnegative().default(0),
  duration_s: durationS.default(0),
  unit: z.string().optional(),
});

const rangeConfig = z
  .object({
    lo: z.coerce.number(),
    hi: z.coerce.number(),
    inside_is_alarm: z.boolean().default(false),
    hysteresis: z.coerce.number().nonnegative().default(0),
    duration_s: durationS.default(0),
  })
  .refine((v) => v.lo < v.hi, { message: "lo must be < hi" });

const booleanConfig = z.object({
  alarm_when: z.boolean(),
  duration_s: durationS.default(0),
});

const inactivityConfig = z.object({
  timeout_s: positiveWindowS,
});

const stuckConfig = z.object({
  tolerance: z.coerce.number().nonnegative(),
  window_s: windowS.default(300),
});

const rateConfig = z.object({
  max_rate: z.coerce.number().positive(),
  window_s: windowS.default(60),
  direction: rateDirectionSchema,
});

const deltaConfig = z.object({
  max_delta: z.coerce.number().positive(),
  window_s: windowS.default(60),
});

const timeframeSchema = z
  .string()
  .regex(/^[0-9]+(s|m|h|d)$/, { message: "timeframe like '15m', '1h', '24h', '7d'" });

const dailyEnergyConfig = z.object({
  operator: numericOperatorSchema,
  threshold: z.coerce.number(),
  timeframe: timeframeSchema.default("24h"),
});

const excessiveCyclingConfig = z.object({
  max_cycles: z.coerce.number().int().nonnegative(),
  timeframe: timeframeSchema.default("1h"),
  power_threshold: z.coerce.number().nonnegative(),
});

const peakDemandConfig = z.object({
  demand_limit: z.coerce.number().positive(),
  warning_percent: z.coerce.number().gt(0).lte(100).default(80),
  duration_s: durationS.default(0),
});

const idleConsumptionConfig = z.object({
  power_threshold: z.coerce.number(),
  weeklySchedule: weeklySchedule7x24,
  duration_s: durationS.default(60),
});

const expressionConfig = z.object({
  expression: expressionSourceSchema,
  datapoints: z.record(z.string().regex(/^[A-Za-z_][A-Za-z0-9_]*$/), expressionDatapointSchema),
  duration_s: durationS.default(0),
});

const compositeConfig = z
  .object({
    operator: compositeOperatorSchema,
    child_rule_ids: z.array(z.string().min(1)).min(2),
    treat_missing_as: compositeMissingModeSchema,
    duration_s: durationS.default(0),
  })
  .refine(
    (v) => new Set(v.child_rule_ids).size === v.child_rule_ids.length,
    { message: "child_rule_ids must be unique" },
  );

const commDegradationConfig = z.object({
  expected_rate_hz: z.coerce.number().positive(),
  window_s: positiveWindowS.default(60),
  loss_pct_threshold: z.coerce.number().gt(0).lte(100),
  duration_s: durationS.default(0),
});

// ---------------------------------------------------------------------------
// Per-rule schema (discriminated union by `type`)
// ---------------------------------------------------------------------------

const commonRuleFields = {
  id: z.string().min(1),
  template_id: z.string().min(1).optional(),
  name: z.string().min(1),
  enabled: z.boolean().default(true),
  severity: severitySchema.default("WARNING"),
  alarm_message: z.string().default(""),
  deliveryMethod: deliveryMethodSchema,
  description: z.string().optional(),
  active_when: activeWhenSchema.optional(),
  createdAt: z.number().int().nonnegative().optional(),
  updatedAt: z.number().int().nonnegative().optional(),
  // v4 fields. All optional; an absent value means "default" so a v3
  // payload migrated forward without an edit keeps its today behaviour.
  // The edge applies the documented defaults: auto_clear=true,
  // propagate="TENANT", notification=null (no email).
  auto_clear: z.boolean().default(true),
  propagate: propagateSchema,
  notification: cloudNotificationSchema.nullable().optional(),
};

const datapointRequired = z.string().min(1);

export const alarmRuleSchema = z.discriminatedUnion("type", [
  z.object({
    ...commonRuleFields,
    type: z.literal("threshold"),
    datapoint: datapointRequired,
    config: thresholdConfig,
  }),
  z.object({
    ...commonRuleFields,
    type: z.literal("range"),
    datapoint: datapointRequired,
    config: rangeConfig,
  }),
  z.object({
    ...commonRuleFields,
    type: z.literal("boolean"),
    datapoint: datapointRequired,
    config: booleanConfig,
  }),
  z.object({
    ...commonRuleFields,
    type: z.literal("inactivity"),
    datapoint: datapointRequired,
    config: inactivityConfig,
  }),
  z.object({
    ...commonRuleFields,
    type: z.literal("stuck"),
    datapoint: datapointRequired,
    config: stuckConfig,
  }),
  z.object({
    ...commonRuleFields,
    type: z.literal("rate_of_change"),
    datapoint: datapointRequired,
    config: rateConfig,
  }),
  z.object({
    ...commonRuleFields,
    type: z.literal("delta"),
    datapoint: datapointRequired,
    config: deltaConfig,
  }),
  z.object({
    ...commonRuleFields,
    type: z.literal("daily_energy"),
    datapoint: datapointRequired,
    config: dailyEnergyConfig,
  }),
  z.object({
    ...commonRuleFields,
    type: z.literal("excessive_cycling"),
    datapoint: datapointRequired,
    config: excessiveCyclingConfig,
  }),
  z.object({
    ...commonRuleFields,
    type: z.literal("peak_demand"),
    datapoint: datapointRequired,
    config: peakDemandConfig,
  }),
  z.object({
    ...commonRuleFields,
    type: z.literal("idle_consumption"),
    datapoint: datapointRequired,
    config: idleConsumptionConfig,
  }),
  z.object({
    ...commonRuleFields,
    type: z.literal("expression"),
    datapoint: z.string().optional(),
    config: expressionConfig,
  }),
  z.object({
    ...commonRuleFields,
    type: z.literal("composite"),
    datapoint: z.string().optional(),
    config: compositeConfig,
  }),
  z.object({
    ...commonRuleFields,
    type: z.literal("comm_degradation"),
    datapoint: datapointRequired,
    config: commDegradationConfig,
  }),
]);

export type AlarmRule = z.infer<typeof alarmRuleSchema>;

export const alarmRuleEnvelopeSchema = z.object({
  version: z
    .union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)])
    .default(2),
  updatedAt: z.number().int().nonnegative().default(0),
  rules: z.array(alarmRuleSchema).default([]),
});

export type AlarmRuleEnvelope = z.infer<typeof alarmRuleEnvelopeSchema>;

// ---------------------------------------------------------------------------
// Status echo (edge → cloud)
// ---------------------------------------------------------------------------

export const alarmRulesStatusSchema = z.object({
  loaded: z.number().int().nonnegative(),
  errors: z.array(
    z.object({
      id: z.string().nullable().optional(),
      error: z.string(),
    }),
  ),
  loaded_at: z.number().int().nonnegative(),
});

export type AlarmRulesStatus = z.infer<typeof alarmRulesStatusSchema>;

// ---------------------------------------------------------------------------
// Available datapoints catalog (edge → cloud)
// ---------------------------------------------------------------------------

export const datapointInfoSchema = z.object({
  topic: z.string().min(1),
  data_type: z.enum(["number", "boolean", "string", "unknown"]).default("unknown"),
  last_value: z.unknown(),
  last_seen_ts: z.number().int().nonnegative().default(0),
  sample_count: z.number().int().nonnegative().default(0),
  unit: z.string().nullable().optional(),
  label: z.string().nullable().optional(),
  category: z.string().default("other"),
  stale: z.boolean().default(false),
  observed_range: z
    .object({
      min: z.number(),
      max: z.number(),
      p5: z.number(),
      p95: z.number(),
      count: z.number().int().nonnegative(),
    })
    .optional(),
  enum_hints: z
    .union([
      z.object({
        type: z.literal("boolean"),
        true_label: z.string(),
        false_label: z.string(),
      }),
      z.object({
        type: z.literal("enum"),
        values: z.array(z.string()),
      }),
    ])
    .optional(),
});

export type DatapointInfo = z.infer<typeof datapointInfoSchema>;

export const availableDatapointsEnvelopeSchema = z.object({
  version: z.union([z.literal(1), z.literal(2)]).default(1),
  updatedAt: z.number().int().nonnegative().default(0),
  engine_version: z.string().optional(),
  datapoints: z.array(datapointInfoSchema).default([]),
});

export type AvailableDatapointsEnvelope = z.infer<
  typeof availableDatapointsEnvelopeSchema
>;

// ---------------------------------------------------------------------------
// Parse helpers — never throw, always return a sane fallback so the SPA
// keeps rendering against malformed cloud state.
// ---------------------------------------------------------------------------

export function parseAlarmRuleEnvelope(raw: unknown): AlarmRuleEnvelope {
  if (raw == null) return { version: 2, updatedAt: 0, rules: [] };
  // Lenient outer parse: keep good rules even if some are bad.
  if (typeof raw !== "object") {
    return { version: 2, updatedAt: 0, rules: [] };
  }
  const obj = raw as Record<string, unknown>;
  const version = (obj.version as 1 | 2 | 3) ?? 2;
  const updatedAt = typeof obj.updatedAt === "number" ? obj.updatedAt : 0;
  const rulesRaw = Array.isArray(obj.rules) ? obj.rules : [];
  const rules: AlarmRule[] = [];
  for (const r of rulesRaw) {
    const parsed = alarmRuleSchema.safeParse(r);
    if (parsed.success) {
      rules.push(parsed.data);
    } else {
      // We deliberately swallow — bad rules are surfaced separately via
      // the edge ACK errors[]. Logging once helps debug live tenants.
      console.warn("[alarmRules] dropping invalid rule from envelope", {
        rule: r,
        issues: parsed.error.issues,
      });
    }
  }
  const env = { version, updatedAt, rules } as AlarmRuleEnvelope;
  return env;
}

export function parseAlarmRulesStatus(raw: unknown): AlarmRulesStatus | null {
  const parsed = alarmRulesStatusSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

export function parseAvailableDatapoints(raw: unknown): AvailableDatapointsEnvelope {
  if (raw == null) {
    return { version: 1, updatedAt: 0, datapoints: [] };
  }
  const parsed = availableDatapointsEnvelopeSchema.safeParse(raw);
  if (parsed.success) {
    return parsed.data;
  }
  console.warn("[alarmRules] available_datapoints failed schema parse", {
    issues: parsed.error.issues,
  });
  return { version: 1, updatedAt: 0, datapoints: [] };
}

// ---------------------------------------------------------------------------
// ID generation
// ---------------------------------------------------------------------------

function _shortId(): string {
  return Math.random().toString(36).slice(2, 10);
}

/** Stable rule id matching the spec pattern `rule_<8>_<8>`. */
export function generateRuleId(): string {
  return `rule_${_shortId()}_${_shortId()}`;
}

/** Stable template id matching `tpl_<8>_<8>`. */
export function generateTemplateId(): string {
  return `tpl_${_shortId()}_${_shortId()}`;
}

// ---------------------------------------------------------------------------
// Attribute keys re-export (single import surface for callers)
// ---------------------------------------------------------------------------

export {
  RULES_ATTRIBUTE_KEY,
  RULES_STATUS_ATTRIBUTE_KEY,
  AVAILABLE_DATAPOINTS_ATTRIBUTE_KEY,
  ALLOWED_EXPRESSION_CALLS,
};
